import * as XLSX from 'xlsx';
import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { Box, Button, Dialog, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import CloseModalButton from "../../../../components/CloseModalButton";
import { StyledDialogFooter, StyledDialogTitle, StyledLargeDialogContent } from "../../../../components/Dialog.styled";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Autoclave, AutoclaveType, Frequency, SporetestingResult } from '../types';
import { downloadFile } from '../../../../helpers/downloadFile';
import { addError } from '../../../../redux/alerts';
import { useAppDispatch } from '../../../../redux/store';
import { AutoclaveTypeId } from '../enums';
import { getAutoclaveFrequency, getAutoclaveTypeName } from '../utils/helpers';
import { CustomerState } from '../../../../redux/customer';
import { ChildCustomerState } from '../../../../redux/childCustomer';
import { downloadReport } from '../../reports/service';

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

const initialFormState = {
  dateType: 'all',
  fileType: 'pdf',
  fromDate: null,
  toDate: null
};

interface DownloadSporetestModalProps {
  autoclaveId?: string | number;
  autoclaves: Autoclave[];
  autoclaveTypeById: Map<AutoclaveTypeId, AutoclaveType>;
  autoclaveFrequencyById: Map<number, Frequency>;
  sporetestsByAutoclaveId: Map<number, SporetestingResult[]>;
  customer: CustomerState | ChildCustomerState
  customerNumber: string;
  handleClose: () => void;
  open: boolean;
  unassignedTestResults: SporetestingResult[];

}

const DownloadSporetestModal = (props: DownloadSporetestModalProps) => {
  const { autoclaveId, autoclaves, autoclaveTypeById, autoclaveFrequencyById, sporetestsByAutoclaveId, customer, customerNumber, handleClose, open, unassignedTestResults } = props
  const [formData, setFormData] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState<{ fromDate?: string; toDate?: string }>({});
  const [loading, setLoading] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      const from = dayjs(updated.fromDate);
      const to = dayjs(updated.toDate);

      if (field === 'dateType' && value === 'all') {
        updated.fromDate = null;
        updated.toDate = null;

        setFormErrors({});
      }

      if (field === 'fromDate' || field === 'toDate') {
        const errors: { fromDate?: string; toDate?: string } = {};

        if (field === 'fromDate' && to.isValid() && from.isAfter(to)) {
          errors.fromDate = 'From date cannot be after To date';
        }

        if (field === 'toDate' && from.isValid() && to.isBefore(from)) {
          errors.toDate = 'To date cannot be before From date';
        }

        setFormErrors(errors);
      }

      return updated;
    });
  };

  const isDisabled = useMemo(() => {
    const hasErrors = Boolean(formErrors.fromDate || formErrors.toDate);
    const missingDates = formData.dateType === 'dateRange' && (!formData.fromDate || !formData.toDate);

    return loading || hasErrors || missingDates;
  }, [
    loading,
    formErrors.fromDate,
    formErrors.toDate,
    formData.dateType,
    formData.fromDate,
    formData.toDate
  ]);

  const handleSubmit = async () => {
    const { dateType, fileType, fromDate, toDate } = formData;

    if (fileType === 'pdf') {
      const options = {
        ...(dateType === 'dateRange' ? { fromDate: dayjs(fromDate).format('YYYY-MM-DD'), toDate: dayjs(toDate).format('YYYY-MM-DD') } : {}),
        ...(autoclaveId ? { autoclaveId: Number(autoclaveId) } : {})
      };

      try {
        setLoading(true);

        const blob = await downloadReport(customerNumber, options)
        const url = URL.createObjectURL(blob)
        const fileName = 'biological_report.pdf'

        await downloadFile({ url, fileName, isBlobURL: true })

        setLoading(false);
        handleClose()
      } catch (error) {
        console.error(error);
        setLoading(false);

        dispatch(addError('There was an error downloading the report, please try again later'));
        return;
      }
    } else {
      const wb = XLSX.utils.book_new();

      const fileName = `${customer?.state?.substring(0, 2)}_${customer?.name?.substring(0, 10)}_${customer.customer_number}`;

      const customerData = [
        { A: customer?.name?.trim() },
        { A: customer?.doctor_name?.trim() },
        { A: customer?.street?.trim() },
        { A: `${customer?.city?.trim()}, ${customer?.state?.trim()} ${customer?.zip?.trim()}` },
        { A: `Customer #: ${customer?.customer_number?.trim()}` },
      ];

      const unassignedTests = unassignedTestResults
        .sort((a, b) => (b?.test_date ?? '').localeCompare((a?.test_date ?? '')))
        .map((test) => {
          return {
            'Test Date': test.sample_date ? dayjs(test.sample_date).format('MM/DD/YYYY') : '',
            'Operator': test.test_operator ? test.test_operator : '',
            'Result Date': test.test_date ? dayjs(test.test_date).format('MM/DD/YYYY') : '',
            'Description': test.result_description,
            'Addressed On': test.addressed_on ? dayjs(test.addressed_on.slice(0, 10)).format('MM/DD/YYYY') : '',
            'Note': test.note ? test.note : ''
          }
        })

      const autoclaveData = autoclaves
        .sort((a, b) => (a.autoclave_name ?? '').localeCompare(b.autoclave_name ?? ''))
        .map(autoclave => {
          const name = autoclave.autoclave_name
          const serial = autoclave.autoclave_serial
          const type = getAutoclaveTypeName(autoclave, autoclaveTypeById)
          const brand = autoclave.autoclave_brand
          const model = autoclave.autoclave_model
          const frequency = getAutoclaveFrequency(autoclave, autoclaveFrequencyById)
          const status = autoclave.active ? 'Active' : 'Inactive'   

          const autoclaveTestResults = sporetestsByAutoclaveId.get(Number(autoclave.autoclave_id)) ?? []

          const details = [
            { A: '' },
            { A: 'Autoclave Name:', B: name },
            { A: 'Serial/Sterilizer #:', B: serial },
            { A: 'Type:', B: type },
            { A: 'Brand:', B: brand },
            { A: 'Model:', B: model },
            { A: 'Frequency:', B: frequency },
            { A: 'Status:', B: status },
            { A: '' }
          ];

          if (autoclaveTestResults.length) {
            const tests = autoclaveTestResults
              .filter(test => {
                if (fromDate && toDate) {
                  const testDate = dayjs(test.test_date)
                  const from = dayjs(fromDate)
                  const to = dayjs(toDate)
                  
                  return testDate.isSameOrAfter(from) && testDate.isSameOrBefore(to)                  
                } else {
                  return true
                }
              })
              .sort((a, b) => String(b.test_date).localeCompare(String(a.test_date)))
              .map(testResult => {
                const test = {
                  A: testResult.sample_date ? dayjs(testResult.sample_date).format('MM/DD/YYYY') : '',
                  B: testResult.received_date ? dayjs(testResult.received_date.slice(0, 10)).format('MM/DD/YYYY') : '',
                  C: testResult.test_date ? dayjs(testResult.test_date.slice(0, 10)).format('MM/DD/YYYY') : '',
                  D: testResult.test_number ? testResult.test_number : '',
                  E: testResult.test_operator ? testResult.test_operator : 'Unknown',
                  F: testResult.test_result,
                  G: testResult.result_description,
                  H: testResult.addressed_on ? dayjs(testResult.addressed_on.slice(0, 10)).format('MM/DD/YYYY') : '',
                  I: testResult.note ? testResult.note : '',
                }

                return test
              })

            return { details, tests }
          } else {
            return { details, tests: [{ A: 'There are no tests for this unit' }] }
          }
        })

      const exportData = {
        fileName,
        customerData,
        unassignedTests,
        autoclaveData,
      }

      const testHeader = [
        {
          A: 'Test Date',
          B: 'Received Date',
          C: 'Result Date',
          D: 'Test Number',
          E: 'Operator',
          F: 'Result',
          G: 'Description',
          H: 'Addressed On',
          I: 'Note',
        }
      ]      

      const ws = XLSX.utils.json_to_sheet(exportData.customerData, { skipHeader: true })

      if (exportData.unassignedTests.length > 0) {
        XLSX.utils.sheet_add_json(ws, [{ A: 'Unassigned Test Results' }], { skipHeader: true, origin: "A8" })
        XLSX.utils.sheet_add_json(ws, exportData.unassignedTests, { origin: "A10" })
      }

      exportData.autoclaveData.forEach(autoclave => {
        const { details, tests } = autoclave

        XLSX.utils.sheet_add_json(ws, details, { skipHeader: true, origin: -1 })
        XLSX.utils.sheet_add_json(ws, testHeader, { skipHeader: true, origin: -1 })
        XLSX.utils.sheet_add_json(ws, tests, { skipHeader: true, origin: -1 })
      })

      XLSX.utils.book_append_sheet(wb, ws, exportData.fileName)
      XLSX.writeFile(wb, `${exportData.fileName}.xlsx`)

      handleClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <CloseModalButton handleClose={handleClose} />
      <StyledDialogTitle>
        Download Spore Test Results
      </StyledDialogTitle>
      <StyledLargeDialogContent>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>File Type</FormLabel>
            <RadioGroup row onChange={(event) => handleChange('fileType', event.target.value)} value={formData.fileType} name="fileType">
              <FormControlLabel value="pdf" control={<Radio size="small" />} label="PDF" />
              <FormControlLabel value="excel" control={<Radio size="small" />} label="Excel" />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <FormLabel>Download</FormLabel>
            <RadioGroup row onChange={(event) => handleChange('dateType', event.target.value)} value={formData.dateType} name="dateType">
              <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
              <FormControlLabel value="dateRange" control={<Radio size="small" />} label="Date Range" />
            </RadioGroup>
          </FormControl>
          {formData.dateType === 'dateRange' && (
            <>
              <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                  <DatePicker
                    label="From Date"
                    maxDate={dayjs()}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: Boolean(formErrors.fromDate),
                        helperText: formErrors.fromDate || '',
                      }
                    }}
                    value={dayjs(formData?.fromDate) ?? null}
                    onChange={(newDate) => handleChange('fromDate', String(newDate))}
                    format="MM-DD-YYYY"
                  />
                </LocalizationProvider>
              </FormControl>
              <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                  <DatePicker
                    label="To Date"
                    maxDate={dayjs()}
                    slotProps={{
                      textField: {
                        size: 'small', error: Boolean(formErrors.toDate),
                        helperText: formErrors.toDate || '',
                      }
                    }}
                    value={dayjs(formData?.toDate) ?? null}
                    onChange={(newDate) => handleChange('toDate', String(newDate))}
                    format="MM-DD-YYYY"
                  />
                </LocalizationProvider>
              </FormControl>
            </>
          )}
        </Box>
      </StyledLargeDialogContent>
      <StyledDialogFooter>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isDisabled}>
          {loading ? 'Submitting...' : 'Submit'}
        </Button>
      </StyledDialogFooter>
    </Dialog>
  )
}

export default DownloadSporetestModal;